<template>
  <div v-if="showBody">
    <Card style="box-shadow: none; border: none;">
      <template #content>
        <form @submit.prevent="login">
          <div class="input-group mb-2 mt-2">
            <span id="email-addon" class="input-group-text"><i class="fas fa-user"></i></span>
            <input
              v-model.trim.lazy="username"
              type="email"
              autocomplete="email"
              class="form-control"
              placeholder="Email"
              name="username"
              aria-label="Email"
              aria-describedby="email-addon"
              required
              aria-required="true"
            />
          </div>
          <div class="input-group mb-2">
            <span id="password-addon" class="input-group-text"><i class="fas fa-lock"></i></span>
            <input
              type="password"
              autocomplete="current-password"
              class="form-control"
              placeholder="Password"
              name="password"
              aria-label="Password"
              aria-describedby="password-addon"
              required
              minlength="8"
              :oninput="() => (state.error = null)"
              aria-required="true"
            />
          </div>
          <!-- show any messages that come back with authentication -->
          <div v-if="state.error" class="alert alert-danger">
            <i class="fas fa-exclamation-circle"></i>
            &nbsp;{{ state.error }}
          </div>
          <VueTurnstile v-if="config.turnstile.enabled" ref="turnstile" v-model="token" :siteKey="config.turnstile.sitekey" />
          <div class="w-100 d-flex justify-content-center justify-content-md-start">
            <button type="submit" class="btn btn-speedify-blue my-3 mx-auto w-100 align-self-center stm-btn-login-local">
              Sign in
            </button>
          </div>
          <div class="w-100 text-center">
            <small>
              <router-link to="/password/forgot" class="text-decoration-none speedify-blue">Forgot your password?</router-link>
            </small>
          </div>
        </form>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts" charset="utf-8">
import { reactive, ref, watch } from 'vue';
import { useRoute, useRouter, LocationQueryValue, RouteLocationRaw } from 'vue-router';
import wretch from 'wretch';
import { useReCaptcha } from 'vue-recaptcha-v3';
import Card from 'primevue/card';
import VueTurnstile from 'vue-turnstile';

import { config } from '@/config';
import { loginRedirect } from '@/services/navigationGuards';
import { useDialogStore } from '@/store/dialog';

// NOTE: optinal because of a bug https://github.com/Particular/ServicePulse/pull/1839
withDefaults(defineProps<{ showBody?: boolean }>(), { showBody: true });

const route = useRoute();
const router = useRouter();
const dialogStore = useDialogStore();
const hint = route.query.login_hint as LocationQueryValue;
let redirect = (route.query.redirect as LocationQueryValue) ?? '/';
const username = ref(hint && decodeURIComponent(hint));

const state = reactive<{
  error: string | null;
}>({
  error: null,
});

// @ts-ignore
let turnstileResp: null | string = null; // eslint-disable-line @typescript-eslint/no-unused-vars
const reCaptchaInstance = config.recaptcha.enabled ? useReCaptcha() : null;
const turnstile = ref<typeof VueTurnstile>();
const token = ref<string>('');

watch(token, (value) => turnstileResp = value ?? null)

const login = async (event: Event) => {
  const formdata = new FormData(event.target as HTMLFormElement);
  if (reCaptchaInstance) {
    await reCaptchaInstance?.recaptchaLoaded();
    const recaptchaResp = await reCaptchaInstance?.executeRecaptcha('login');
    recaptchaResp && formdata.set('g-recaptcha-response', recaptchaResp);
  }
  // automatic
  // turnstileResp && formdata.set('cf-turnstile-response', turnstileResp);
  await wretch('/login')
    .post(Object.fromEntries(formdata))
    .unauthorized(err => {
      state.error = err.json?.message;
      // Each reCAPTCHA user response token is valid for two minutes, and can
      // only be verified once to prevent replay attacks. If you need a new
      // token, you can re-run the reCAPTCHA verification.
      //
      // Otherwise we could receive the response body:
      // { success: false, 'error-codes': [ 'timeout-or-duplicate' ] }
      //
      // https://developers.google.com/recaptcha/docs/verify#token_restrictions
      config.turnstile.enabled && (turnstile as any).value.reset();
    })
    .internalError(err => {
      state.error = err.json?.message;

      config.turnstile.enabled && (turnstile as any).value.reset();
    })
    .res(() => {
      dialogStore.$reset();
      // handle redirection back to external urls like order.speedify if signing in from there
      void router.replace(loginRedirect(redirect) as RouteLocationRaw);
    })
    .catch(async () => {
      dialogStore.$reset();
      void await router.replace('/');
    });
};
</script>
